/* ==========================================================================
   At-Risk Pharmacies Table Component
   Task F37: Table showing pharmacies flagged as at-risk for churn
   ========================================================================== */

import { CheckCircle } from 'lucide-react'
import type { AtRiskListResponse, AtRiskPharmacy } from '@/api/internal'

const FLAG_STYLES: Record<string, string> = {
  no_submissions_14d: 'bg-red-100 text-red-800',
  low_providers_30d: 'bg-amber-100 text-amber-800',
}

const FLAG_LABELS: Record<string, string> = {
  no_submissions_14d: 'No Submissions (14d+)',
  low_providers_30d: 'Low Providers (30d+)',
}

function FlagBadge({ reason }: { reason: string }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${FLAG_STYLES[reason] || 'bg-gray-100 text-gray-800'}`}
    >
      {FLAG_LABELS[reason] || reason}
    </span>
  )
}

function SkeletonTable() {
  return (
    <div className="bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-6 animate-pulse">
      <div className="h-6 w-48 bg-gray-200 rounded mb-4" />
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-10 bg-gray-100 rounded" />
        ))}
      </div>
    </div>
  )
}

interface AtRiskTableProps {
  data: AtRiskListResponse | null
  loading: boolean
}

export function AtRiskTable({ data, loading }: AtRiskTableProps) {
  if (loading) return <SkeletonTable />
  if (!data) return null

  return (
    <div className="bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-charcoal mb-4">
        At-Risk Accounts{' '}
        {data.count > 0 && (
          <span className="text-sm font-normal text-charcoal/60">
            ({data.count})
          </span>
        )}
      </h2>

      {data.count === 0 ? (
        <div className="flex items-center gap-2 py-8 justify-center text-green-700">
          <CheckCircle className="h-5 w-5" />
          <span className="text-sm font-medium">No at-risk accounts.</span>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:-mx-6">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left text-xs font-medium text-charcoal/60 uppercase tracking-wider px-4 sm:px-6 py-3">
                  Pharmacy
                </th>
                <th className="text-left text-xs font-medium text-charcoal/60 uppercase tracking-wider px-4 sm:px-6 py-3">
                  Slug
                </th>
                <th className="text-left text-xs font-medium text-charcoal/60 uppercase tracking-wider px-4 sm:px-6 py-3">
                  Days Live
                </th>
                <th className="text-left text-xs font-medium text-charcoal/60 uppercase tracking-wider px-4 sm:px-6 py-3">
                  Flag
                </th>
                <th className="text-right text-xs font-medium text-charcoal/60 uppercase tracking-wider px-4 sm:px-6 py-3">
                  Providers
                </th>
                <th className="text-right text-xs font-medium text-charcoal/60 uppercase tracking-wider px-4 sm:px-6 py-3">
                  Rxs
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.pharmacies.map((p: AtRiskPharmacy) => (
                <tr key={p.pharmacy_id} className="hover:bg-gray-50">
                  <td className="px-4 sm:px-6 py-3 text-sm font-medium text-charcoal">
                    {p.pharmacy_name}
                  </td>
                  <td className="px-4 sm:px-6 py-3 text-sm text-charcoal/70 font-mono">
                    {p.slug}
                  </td>
                  <td className="px-4 sm:px-6 py-3 text-sm text-charcoal/70">
                    {p.days_since_golive}
                  </td>
                  <td className="px-4 sm:px-6 py-3">
                    <FlagBadge reason={p.flag_reason} />
                  </td>
                  <td className="px-4 sm:px-6 py-3 text-sm text-charcoal/70 text-right">
                    {p.provider_count}
                  </td>
                  <td className="px-4 sm:px-6 py-3 text-sm text-charcoal/70 text-right">
                    {p.prescription_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
